import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { Contact, Category } from '../types';

// Loading Skeleton Row
const SkeletonRow: React.FC = () => (
  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
    <td style={{ padding: '16px 24px' }}><div style={{ height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '80%' }} /></td>
    <td style={{ padding: '16px 24px' }}><div style={{ height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '60%' }} /></td>
    <td style={{ padding: '16px 24px' }}><div style={{ height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '50%' }} /></td>
    <td style={{ padding: '16px 24px' }}><div style={{ height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '40%' }} /></td>
    <td style={{ padding: '16px 24px' }}><div style={{ height: '24px', background: '#e5e7eb', borderRadius: '9999px', width: '60px' }} /></td>
    <td style={{ padding: '16px 24px' }}><div style={{ height: '32px', background: '#e5e7eb', borderRadius: '6px', width: '80px' }} /></td>
  </tr>
);

const Contacts: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBulkSendModal, setShowBulkSendModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditCategoryId, setBulkEditCategoryId] = useState('');
  const [importing, setImporting] = useState(false);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [newContact, setNewContact] = useState({
    email: '',
    first_name: '',
    last_name: '',
    company: '',
    mobile_number: '',
    website: '',
    address: '',
    city: '',
    category_id: '',
  });
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importCategoryId, setImportCategoryId] = useState('');
  const [importFieldMapping, setImportFieldMapping] = useState({
    email: '',
    first_name: '',
    last_name: '',
    company: '',
    mobile_number: '',
    website: '',
    city: '',
  });
  const [bulkSendData, setBulkSendData] = useState({
    template_id: '',
    sender_profile_id: '',
  });
  const [templates, setTemplates] = useState<any[]>([]);
  const [senderProfiles, setSenderProfiles] = useState<any[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 350);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (selectedCategory) params.category_id = selectedCategory;
      if (selectedStatus) params.status = selectedStatus;
      params.page = currentPage;
      params.per_page = perPage;

      const response = await api.get('/contacts', params);
      setContacts(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch contacts', error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, selectedCategory, selectedStatus, currentPage, perPage]);

  // Reset page when search or category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedCategory, selectedStatus]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories', error);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    fetchCategories();
    fetchTemplates();
    fetchSenderProfiles();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/templates', { active_only: true });
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch templates', error);
    }
  };

  const fetchSenderProfiles = async () => {
    try {
      const response = await api.get('/sender-profiles');
      setSenderProfiles(response.data || []);
    } catch (error) {
      console.error('Failed to fetch sender profiles', error);
    }
  };

  const isSearchPending = search !== debouncedSearch;

  const handleSaveContact = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingContact) {
        await api.put(`/contacts/${editingContact.id}`, newContact);
      } else {
        await api.post('/contacts', newContact);
      }
      setShowAddModal(false);
      setEditingContact(null);
      setNewContact({ email: '', first_name: '', last_name: '', company: '', mobile_number: '', website: '', address: '', city: '', category_id: '' });
      fetchContacts();
    } catch (error) {
      alert(editingContact ? 'Failed to update contact' : 'Failed to add contact');
    }
  };

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    setNewContact({
      email: contact.email || '',
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      company: contact.company || '',
      mobile_number: contact.mobile_number || '',
      website: contact.website || '',
      address: contact.address || '',
      city: contact.city || '',
      category_id: contact.category_id ? String(contact.category_id) : '',
    });
    setShowAddModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await api.delete(`/contacts/${id}`);
      fetchContacts();
    } catch (error) {
      alert('Failed to delete contact');
    }
  };

  const handleBulkEditCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedContactIds.length === 0) {
      alert('Please select at least one contact');
      return;
    }
    if (!bulkEditCategoryId) {
      alert('Please select a category');
      return;
    }
    if (!window.confirm(`Are you sure you want to update category for ${selectedContactIds.length} contacts?`)) {
      return;
    }
    try {
      await api.post('/contacts/bulk-update-category', {
        contact_ids: selectedContactIds,
        category_id: parseInt(bulkEditCategoryId),
      });
      alert('Category updated successfully!');
      setShowBulkEditModal(false);
      setBulkEditCategoryId('');
      setSelectedContactIds([]);
      setSelectAll(false);
      fetchContacts();
    } catch (error) {
      alert('Failed to update category');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedContactIds.length === 0) {
      alert('Please select at least one contact');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedContactIds.length} contacts?`)) {
      return;
    }
    try {
      await api.post('/contacts/bulk-delete', {
        contact_ids: selectedContactIds,
      });
      alert('Contacts deleted successfully!');
      setSelectedContactIds([]);
      setSelectAll(false);
      fetchContacts();
    } catch (error) {
      alert('Failed to delete contacts');
    }
  };

  const handleSelectContact = (id: number) => {
    setSelectedContactIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedContactIds([]);
    } else {
      setSelectedContactIds(contacts.map(c => c.id));
    }
    setSelectAll(!selectAll);
  };

  const handleBulkSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedContactIds.length === 0) {
      alert('Please select at least one contact');
      return;
    }
    if (!bulkSendData.template_id || !bulkSendData.sender_profile_id) {
      alert('Please select a template and sender profile');
      return;
    }
    if (!window.confirm(`Are you sure you want to send emails to ${selectedContactIds.length} contacts?`)) {
      return;
    }
    setSendingBulk(true);
    try {
      const response = await api.post('/campaigns', {
        name: `Bulk Send - ${new Date().toLocaleDateString()}`,
        template_id: parseInt(bulkSendData.template_id),
        sender_profile_id: parseInt(bulkSendData.sender_profile_id),
        contact_ids: selectedContactIds,
      });
      
      // Automatically send the campaign
      await api.post(`/campaigns/${response.data.id}/send`, {});
      
      alert(`Campaign created and emails are being sent to ${selectedContactIds.length} contacts!`);
      setShowBulkSendModal(false);
      setSelectedContactIds([]);
      setSelectAll(false);
      setBulkSendData({ template_id: '', sender_profile_id: '' });
      fetchContacts();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to create or send campaign');
    } finally {
      setSendingBulk(false);
    }
  };

  const handleImport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!importFile) {
      alert('Please select a CSV file');
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      if (importCategoryId) formData.append('category_id', importCategoryId);
      formData.append('field_mapping', JSON.stringify(importFieldMapping));

      const response = await api.post('/contacts/import', formData);
      console.log('Import response:', response.data);
      alert(`Imported ${response.data.imported} contacts successfully!`);
      setShowImportModal(false);
      setImportFile(null);
      setImportCategoryId('');
      setImportFieldMapping({ email: '', first_name: '', last_name: '', company: '', mobile_number: '', website: '', city: '' });
      fetchContacts();
    } catch (error: any) {
      console.error('Import error:', error.response?.data);
      alert(error.response?.data?.error || error.response?.data?.message || 'Failed to import contacts');
    } finally {
      setImporting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'contacted': return { bg: '#d1fae5', text: '#065f46' };
      case 'replied': return { bg: '#dbeafe', text: '#1e40af' };
      case 'unsubscribed': return { bg: '#fee2e2', text: '#991b1b' };
      default: return { bg: '#fef3c7', text: '#92400e' };
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 700, color: '#111827', margin: '0 0 4px 0' }}>Contacts</h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Manage your email contacts and subscribers</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          {selectedContactIds.length > 0 && (
            <>
              <button 
                onClick={() => setShowBulkEditModal(true)}
                style={{ 
                  padding: '12px 20px', 
                  background: '#f59e0b', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>�</span> Set Category
              </button>
              <button 
                onClick={handleBulkDelete}
                style={{ 
                  padding: '12px 20px', 
                  background: '#ef4444', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>🗑️</span> Delete ({selectedContactIds.length})
              </button>
            </>
          )}
          <button 
            onClick={() => {
              setEditingContact(null);
              setNewContact({ email: '', first_name: '', last_name: '', company: '', mobile_number: '', website: '', address: '', city: '', category_id: '' });
              setShowAddModal(true);
            }}
            style={{ 
              padding: '12px 20px', 
              background: '#3b82f6', 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px', 
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>+</span> Add Contact
          </button>
          <button 
            onClick={() => setShowImportModal(true)}
            style={{ 
              padding: '12px 20px', 
              background: '#10b981', 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px', 
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>📥</span> Import CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '250px' }}>
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}>🔍</span>
          <input
            type="text"
            placeholder="Search contacts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ 
              padding: '10px 12px 10px 40px', 
              flex: 1,
              width: '100%',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '0.875rem',
              outline: 'none'
            }}
          />
          {isSearchPending && (
            <span
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6366f1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-label="Searching"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                <path d="M21 12a9 9 0 0 1-9 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 12 12"
                    to="360 12 12"
                    dur="0.8s"
                    repeatCount="indefinite"
                  />
                </path>
              </svg>
            </span>
          )}
        </div>
        <select 
          value={selectedCategory} 
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{ 
            padding: '10px 16px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '0.875rem',
            background: 'white',
            minWidth: '150px'
          }}
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
        <select 
          value={selectedStatus} 
          onChange={(e) => setSelectedStatus(e.target.value)}
          style={{ 
            padding: '10px 16px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '0.875rem',
            background: 'white',
            minWidth: '150px'
          }}
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="contacted">Contacted</option>
          <option value="replied">Replied</option>
          <option value="unsubscribed">Unsubscribed</option>
          <option value="bounced">Bounced</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ 
        background: 'white', 
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</th>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</th>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Company</th>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>City</th>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Category</th>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <>
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                </>
              ) : contacts.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '48px 24px', textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '16px' }}>👤</div>
                    <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                      No contacts found
                    </h3>
                    <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '0.875rem' }}>
                      {search || selectedCategory ? 'Try adjusting your filters' : 'Add your first contact to get started'}
                    </p>
                    {!search && !selectedCategory && (
                      <button 
                        onClick={() => setShowAddModal(true)}
                        style={{ 
                          padding: '10px 20px', 
                          background: '#3b82f6', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                      >
                        Add Contact
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                contacts.map((contact, index) => {
                  const statusColors = getStatusColor(contact.status);
                  return (
                    <tr 
                      key={contact.id} 
                      style={{ 
                        borderBottom: '1px solid #e5e7eb', 
                        background: index % 2 === 0 ? 'white' : '#fafafa',
                        transition: 'background 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.background = index % 2 === 0 ? 'white' : '#fafafa'}
                    >
                      <td style={{ padding: '16px 24px', width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={selectedContactIds.includes(contact.id)}
                          onChange={() => handleSelectContact(contact.id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ 
                            width: '36px', 
                            height: '36px', 
                            borderRadius: '50%', 
                            background: '#3b82f6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.875rem'
                          }}>
                            {(contact.first_name?.[0] || contact.email[0]).toUpperCase()}
                          </div>
                          <span style={{ fontWeight: 500, color: '#111827' }}>
                            {contact.first_name} {contact.last_name}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '16px 24px', color: '#6b7280', fontSize: '0.875rem' }}>{contact.email}</td>
                      <td style={{ padding: '16px 24px', color: '#6b7280', fontSize: '0.875rem' }}>{contact.company || '-'}</td>
                      <td style={{ padding: '16px 24px', color: '#6b7280', fontSize: '0.875rem' }}>{contact.city || '-'}</td>
                      <td style={{ padding: '16px 24px' }}>
                        {contact.category && (
                          <span style={{ 
                            padding: '6px 12px', 
                            borderRadius: '9999px', 
                            background: contact.category.color + '20',
                            color: contact.category.color,
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}>
                            {contact.category.name}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        <span style={{ 
                          padding: '6px 12px', 
                          borderRadius: '9999px',
                          background: statusColors.bg,
                          color: statusColors.text,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}>
                          {contact.status}
                        </span>
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleEdit(contact)}
                            style={{
                              padding: '8px 16px',
                              background: '#eff6ff',
                              color: '#2563eb',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: 500
                            }}
                          >
                            Edit
                          </button>
                          <button 
                            onClick={() => handleDelete(contact.id)}
                            style={{ 
                              padding: '8px 16px', 
                              background: '#fef2f2', 
                              color: '#dc2626', 
                              border: 'none', 
                              borderRadius: '6px', 
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: 500
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginTop: '20px',
          padding: '16px',
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, total)} of {total} contacts
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '8px 16px',
                background: currentPage === 1 ? '#f3f4f6' : '#3b82f6',
                color: currentPage === 1 ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: '0.875rem', color: '#374151', minWidth: '80px', textAlign: 'center' }}>
              Page {currentPage} of {Math.ceil(total / perPage)}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.ceil(total / perPage) >= prev + 1 ? prev + 1 : prev)}
              disabled={currentPage >= Math.ceil(total / perPage)}
              style={{
                padding: '8px 16px',
                background: currentPage >= Math.ceil(total / perPage) ? '#f3f4f6' : '#3b82f6',
                color: currentPage >= Math.ceil(total / perPage) ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: currentPage >= Math.ceil(total / perPage) ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add Contact Modal */}
      {showAddModal && (
        <div style={{ 
          position: 'fixed', 
          top: 0, left: 0, right: 0, bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '16px', 
            width: '100%',
            maxWidth: '480px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', margin: '0 0 24px 0' }}>
              {editingContact ? 'Edit Contact' : 'Add New Contact'}
            </h2>
            <form onSubmit={handleSaveContact}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={newContact.email}
                  onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none'
                  }}
                  placeholder="email@example.com"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                    First Name
                  </label>
                  <input
                    type="text"
                    value={newContact.first_name}
                    onChange={(e) => setNewContact({...newContact, first_name: e.target.value})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      outline: 'none'
                    }}
                    placeholder="John"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={newContact.last_name}
                    onChange={(e) => setNewContact({...newContact, last_name: e.target.value})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      outline: 'none'
                    }}
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Company
                </label>
                <input
                  type="text"
                  value={newContact.company}
                  onChange={(e) => setNewContact({...newContact, company: e.target.value})}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none'
                  }}
                  placeholder="Acme Inc."
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Mobile Number
                </label>
                <input
                  type="tel"
                  value={newContact.mobile_number}
                  onChange={(e) => setNewContact({...newContact, mobile_number: e.target.value})}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none'
                  }}
                  placeholder="+1 234 567 8900"
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Website
                </label>
                <input
                  type="url"
                  value={newContact.website}
                  onChange={(e) => setNewContact({...newContact, website: e.target.value})}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none'
                  }}
                  placeholder="https://example.com"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                    Address
                  </label>
                  <input
                    type="text"
                    value={newContact.address}
                    onChange={(e) => setNewContact({...newContact, address: e.target.value})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      outline: 'none'
                    }}
                    placeholder="123 Main St"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                    City
                  </label>
                  <input
                    type="text"
                    value={newContact.city}
                    onChange={(e) => setNewContact({...newContact, city: e.target.value})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      outline: 'none'
                    }}
                    placeholder="New York"
                  />
                </div>
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Category
                </label>
                <select
                  value={newContact.category_id}
                  onChange={(e) => setNewContact({...newContact, category_id: e.target.value})}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    background: 'white'
                  }}
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingContact(null);
                    setNewContact({ email: '', first_name: '', last_name: '', company: '', mobile_number: '', website: '', address: '', city: '', category_id: '' });
                  }}
                  style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: '#f3f4f6', 
                    color: '#374151',
                    border: 'none', 
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: '#3b82f6', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  {editingContact ? 'Update Contact' : 'Add Contact'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Edit Category Modal */}
      {showBulkEditModal && (
        <div style={{ 
          position: 'fixed', 
          top: 0, left: 0, right: 0, bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '16px', 
            width: '100%',
            maxWidth: '480px'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', margin: '0 0 24px 0' }}>
              Set Category for {selectedContactIds.length} Contacts
            </h2>
            <form onSubmit={handleBulkEditCategory}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Category
                </label>
                <select
                  value={bulkEditCategoryId}
                  onChange={(e) => setBulkEditCategoryId(e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    background: 'white'
                  }}
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  type="button"
                  onClick={() => {
                    setShowBulkEditModal(false);
                    setBulkEditCategoryId('');
                  }}
                  style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: '#f3f4f6', 
                    color: '#374151',
                    border: 'none', 
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: '#f59e0b', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Update Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div style={{ 
          position: 'fixed', 
          top: 0, left: 0, right: 0, bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '16px', 
            width: '100%',
            maxWidth: '560px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', margin: '0 0 24px 0' }}>Import Contacts from CSV</h2>
            <form onSubmit={handleImport}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  CSV File *
                </label>
                <input
                  type="file"
                  accept=".csv"
                  required
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none'
                  }}
                />
                <p style={{ margin: '4px 0 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                  File must include headers: email, first_name, last_name, company, mobile_number, website, city
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Assign to Category
                </label>
                <select
                  value={importCategoryId}
                  onChange={(e) => setImportCategoryId(e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    background: 'white'
                  }}
                >
                  <option value="">No category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Field Mapping (CSV column names)
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: '#6b7280' }}>Email column</label>
                    <input
                      type="text"
                      value={importFieldMapping.email}
                      onChange={(e) => setImportFieldMapping({...importFieldMapping, email: e.target.value})}
                      placeholder="email"
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px', 
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: '#6b7280' }}>First Name column</label>
                    <input
                      type="text"
                      value={importFieldMapping.first_name}
                      onChange={(e) => setImportFieldMapping({...importFieldMapping, first_name: e.target.value})}
                      placeholder="first_name"
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px', 
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: '#6b7280' }}>Last Name column</label>
                    <input
                      type="text"
                      value={importFieldMapping.last_name}
                      onChange={(e) => setImportFieldMapping({...importFieldMapping, last_name: e.target.value})}
                      placeholder="last_name"
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px', 
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: '#6b7280' }}>Company column</label>
                    <input
                      type="text"
                      value={importFieldMapping.company}
                      onChange={(e) => setImportFieldMapping({...importFieldMapping, company: e.target.value})}
                      placeholder="company"
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px', 
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: '#6b7280' }}>Mobile Number column</label>
                    <input
                      type="text"
                      value={importFieldMapping.mobile_number}
                      onChange={(e) => setImportFieldMapping({...importFieldMapping, mobile_number: e.target.value})}
                      placeholder="mobile_number"
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px', 
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: '#6b7280' }}>Website column</label>
                    <input
                      type="text"
                      value={importFieldMapping.website}
                      onChange={(e) => setImportFieldMapping({...importFieldMapping, website: e.target.value})}
                      placeholder="website"
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px', 
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: '#6b7280' }}>City column</label>
                    <input
                      type="text"
                      value={importFieldMapping.city}
                      onChange={(e) => setImportFieldMapping({...importFieldMapping, city: e.target.value})}
                      placeholder="city"
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px', 
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  type="button"
                  onClick={() => setShowImportModal(false)}
                  disabled={importing}
                  style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: '#f3f4f6', 
                    color: '#374151',
                    border: 'none', 
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: importing ? 'not-allowed' : 'pointer',
                    opacity: importing ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={importing}
                  style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: importing ? '#9ca3af' : '#10b981', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: importing ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {importing ? (
                    <>
                      <span style={{ 
                        width: '16px', 
                        height: '16px', 
                        border: '2px solid rgba(255,255,255,0.3)', 
                        borderTopColor: 'white', 
                        borderRadius: '50%', 
                        animation: 'spin 1s linear infinite',
                        display: 'inline-block'
                      }} />
                      Importing...
                    </>
                  ) : (
                    'Import Contacts'
                  )}
                </button>
              </div>
            </form>

            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}

      {showBulkSendModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: '16px', 
            width: '100%',
            maxWidth: '560px'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', margin: '0 0 24px 0' }}>Send Bulk Email</h2>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '24px' }}>
              Sending to {selectedContactIds.length} contact(s)
            </p>
            <form onSubmit={handleBulkSend}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Email Template *
                </label>
                <select
                  value={bulkSendData.template_id}
                  onChange={(e) => setBulkSendData({...bulkSendData, template_id: e.target.value})}
                  required
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    background: 'white'
                  }}
                >
                  <option value="">Select a template</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                  Sender Profile *
                </label>
                <select
                  value={bulkSendData.sender_profile_id}
                  onChange={(e) => setBulkSendData({...bulkSendData, sender_profile_id: e.target.value})}
                  required
                  style={{ 
                    width: '100%', 
                    padding: '10px 14px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    background: 'white'
                  }}
                >
                  <option value="">Select a sender profile</option>
                  {senderProfiles.map(sp => (
                    <option key={sp.id} value={sp.id}>{sp.name} ({sp.from_email})</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  type="button"
                  onClick={() => {
                    setShowBulkSendModal(false);
                    setBulkSendData({ template_id: '', sender_profile_id: '' });
                  }}
                  disabled={sendingBulk}
                  style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: '#f3f4f6', 
                    color: '#374151',
                    border: 'none', 
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: sendingBulk ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={sendingBulk}
                  style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: sendingBulk ? '#9ca3af' : '#8b5cf6', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: sendingBulk ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {sendingBulk ? (
                    <>
                      <span style={{ 
                        width: '16px', 
                        height: '16px', 
                        border: '2px solid rgba(255,255,255,0.3)', 
                        borderTopColor: 'white', 
                        borderRadius: '50%', 
                        animation: 'spin 1s linear infinite',
                        display: 'inline-block'
                      }} />
                      Creating Campaign...
                    </>
                  ) : (
                    'Send Emails'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;
